import { WFSGetFeatureManager } from "@/geoMap/net/WFSGetFeatureManager";
import { FeatureGeoJson } from "../FeatureGeoJson";
import { SelectedFeatureInfo } from "../SelectedFeatureInfo";
import { WMSGetFeatureManager } from "../net/WMSGetFeatureManager";
import { CqlParameter } from "@/geoMap/net/CqlParameter";
import { Feature, Map } from "ol";
import ImageWMSEx from "../net/ImageWMSEx";
import GeoJSON from "ol/format/GeoJSON";
import { ILayerFactory } from "./ILayerFactory";
import ImageLayer from "ol/layer/Image";
import { Fill, Stroke, Style } from "ol/style";
import CircleStyle from "ol/style/Circle";
import {
  TraVosLayerName,
  TraVosLayerType,
  TraVosRoadType,
} from "./TraVosLayerName";
import { TraVosFixHourSearchDateAttributeConvert } from "@/attribute/convert/TraVosFixHourSearchDateAttributeConvert";
import { TraVosCalcHourSearchDateAttributeConvert } from "@/attribute/convert/TraVosCalcHourSearchDateAttributeConvert";
import { TraVosLinkSearchDateAttributeConvert } from "@/attribute/convert/TraVosLinkSearchDateAttributeConvert";
import { TraVosCCDHourSearchDateAttributeConvert } from "@/attribute/convert/TraVosCCDHourSearchDateAttributeConvert";
import { TraVosCCDHourRealTimeTrafficConvert } from "@/attribute/convert/TraVosCCDHourRealTimeTrafficConvert";
import { TraVosFixCCDHourSearchDateAttributeConvert } from "@/attribute/convert/TraVosFixCCDHourSearchDateAttributeConvert";
import { RealTimeTraVosCalcFiveMinSearchDateAttributeConvert } from "@/attribute/convert/RealTimeTraVosCalcFiveMinSearchDateAttributeConvert";
import { RealTimeTraVosCalcHourSearchDateAttributeConvert } from "@/attribute/convert/RealTimeTraVosCalcHourSearchDateAttributeConvert";
import { RealTimeTraVosCCDFiveMinSearchDateAttributeConvert } from "@/attribute/convert/RealTimeTraVosCCDFiveMinSearchDateAttributeConvert";
import { RealTimeTraVosCCDHourSearchDateAttributeConvert } from "@/attribute/convert/RealTimeTraVosCCDHourSearchDateAttributeConvert";
import { ISearchDateAttributeConvert } from "@/attribute/convert/ISearchDateAttributeConvert";
import { AttributeType } from "../define/AttributeType";
import { MapContextMenuItem } from "../MapContextMenuItem";
import { TraVosCalcFiveMinRealTimeTrafficConvert } from "@/attribute/convert/TraVosCalcFiveMinRealTimeTrafficConvert";
import { TraVosCCDFiveMinRealTimeTrafficConvert } from "@/attribute/convert/TraVosCCDFiveMinRealTimeTrafficConvert";
import { TraVosCalcHourRealTimeTrafficConvert } from "@/attribute/convert/TraVosCalcHourRealTimeTrafficConvert";
import { LayerName, getLayerKey } from "../LayerName";
import { IRealTimeTrafficConvert } from "@/attribute/convert/IRealTimeTrafficConvert";
import { ITraVosPlayEvent } from "./ITraVosPlayEvent";
import { LayerUtil } from "../util/LayerUtil";

/**
 * 最大ズームレベル
 */

const LAYER_MAX_ZOOMLEVEL = 18;

/**
 * 最小ズームレベル
 */

const LAYER_MIN_ZOOMLEVEL = 9;

/**
 * 選択中円スタイル 円半径
 */
const CIRCLE_RADIUS = 7;

/**
 * 選択中線スタイル 線幅
 */
const LINE_STRING_WIDTH = 10;
/**
 * API通信用URL
 */
const URL_API = process.env.VUE_APP_XROAD_API_URL;

type SearchDateAttributeConvertInfo = {
  contextMenu: string;
  attributeConvert: ISearchDateAttributeConvert;
};

type RealTimeAttributeConvertInfo = {
  contextMenu: string;
  attributeConvert: IRealTimeTrafficConvert;
};

/**
 * 過去属性変換クラス取得関数
 */
const FUNCTION_getSearchDateAttributeConvert = (
  key: string,
  geoJSON: any,
  layerType: TraVosLayerType,
  roadType: TraVosRoadType,
  regularObservationCd: string,
  commonName: string,
  timeCorrectionFlag: string,
  timecode: string,
  trafficSurveySectionNumber: string
): SearchDateAttributeConvertInfo => {
  switch (key) {
    /** Travos_計測値１時間 */
    case "t_travospublic_measure_1h":
      return {
        contextMenu:
          "交通量データ（過去）_1時間値（常時観測データ計測値）_常設トラカン",
        attributeConvert: new TraVosCalcHourSearchDateAttributeConvert(
          geoJSON,
          layerType,
          roadType,
          regularObservationCd,
          commonName,
          timeCorrectionFlag,
          timecode
        ),
      };
    /** Travos_計測値１時間（画像認識型） */
    case "t_travospublic_measure_1h_img":
      return {
        contextMenu:
          "交通量データ（過去）_1時間値（常時観測データ計測値）_CCTVトラカン",
        attributeConvert: new TraVosCCDHourSearchDateAttributeConvert(
          geoJSON,
          layerType,
          roadType,
          regularObservationCd,
          commonName,
          timeCorrectionFlag,
          timecode
        ),
      };
    /** Travos_確定値１時間 */
    case "t_travospublic_define_1h":
      return {
        contextMenu:
          "交通量データ（過去）_1時間値（常時観測データ確定値）_常設トラカン",
        attributeConvert: new TraVosFixHourSearchDateAttributeConvert(
          geoJSON,
          layerType,
          roadType,
          regularObservationCd,
          commonName,
          timeCorrectionFlag,
          timecode
        ),
      };
    /** Travos_確定値１時間（画像認識型） */
    case "t_travospublic_define_1h_img":
      return {
        contextMenu:
          "交通量データ（過去）_1時間値（常時観測データ確定値）_CCTVトラカン",
        attributeConvert: new TraVosFixCCDHourSearchDateAttributeConvert(
          geoJSON,
          layerType,
          roadType,
          regularObservationCd,
          commonName,
          timeCorrectionFlag,
          timecode
        ),
      };
    /** Travos_交通基本調査区間 */
    case "t_travospublic_traffic_basic_sec":
      return {
        contextMenu:
          "交通量データ（過去）_1時間値（推定値）_常時観測区間および推定区間",
        attributeConvert: new TraVosLinkSearchDateAttributeConvert(
          geoJSON,
          layerType,
          roadType,
          trafficSurveySectionNumber,
          timecode
        ),
      };
  }
};

/**
 * リアルタイム属性変換クラス取得関数
 */
const FUNCTION_getRealTimeAttributeConvert = (
  key: string,
  geoJSON: any,
  layerType: TraVosLayerType,
  roadType: TraVosRoadType,
  regularObservationCd: string,
  commonName: string,
  timeCorrectionFlag: string,
  timecode: string
): SearchDateAttributeConvertInfo => {
  switch (key) {
    /** Travos_計測値５分 */
    case "t_travospublic_measure_5m":
      return {
        contextMenu: "交通量データ（最新）_５分値_常設トラカン",
        attributeConvert:
          new RealTimeTraVosCalcFiveMinSearchDateAttributeConvert(
            geoJSON,
            layerType,
            roadType,
            regularObservationCd,
            commonName,
            timeCorrectionFlag,
            timecode
          ),
      };
    /** Travos_計測値１時間 */
    case "t_travospublic_measure_1h":
      return {
        contextMenu: "交通量データ（最新）_１時間値_常設トラカン",
        attributeConvert: new RealTimeTraVosCalcHourSearchDateAttributeConvert(
          geoJSON,
          layerType,
          roadType,
          regularObservationCd,
          commonName,
          timeCorrectionFlag,
          timecode
        ),
      };
    /** Travos_計測値５分（画像認識型） */
    case "t_travospublic_measure_5m_img":
      return {
        contextMenu: "交通量データ（最新）_５分値_CCTVトラカン",
        attributeConvert:
          new RealTimeTraVosCCDFiveMinSearchDateAttributeConvert(
            geoJSON,
            layerType,
            roadType,
            regularObservationCd,
            commonName,
            timeCorrectionFlag,
            timecode
          ),
      };
    /** Travos_計測値１時間（画像認識型） */
    case "t_travospublic_measure_1h_img":
      return {
        contextMenu: "交通量データ（最新）_１時間値_CCTVトラカン",
        attributeConvert: new RealTimeTraVosCCDHourSearchDateAttributeConvert(
          geoJSON,
          layerType,
          roadType,
          regularObservationCd,
          commonName,
          timeCorrectionFlag,
          timecode
        ),
      };
  }
};

/**
 * リアルタイム交通量表示変換関数
 * @param key レイヤ物理キー
 * @param roadType 道路種別
 * @param regularObservationCd 常時観測点コード
 * @param commonName 通称
 * @param timeCorrctionFlag 収集時間フラグ
 */
const FUNCTION_getRealTimeTrafficConvert = (
  key: string,
  roadType: string,
  regularObservationCd: string,
  commonName: string,
  timeCorrectionFlag: string
): RealTimeAttributeConvertInfo => {
  switch (key) {
    /** Travos_計測値５分 */
    case "t_travospublic_measure_5m":
      return {
        contextMenu: "交通量データ（最新）_５分値_常設トラカン",
        attributeConvert: new TraVosCalcFiveMinRealTimeTrafficConvert(
          roadType,
          regularObservationCd,
          commonName,
          timeCorrectionFlag
        ),
      };
    /** Travos_計測値１時間 */
    case "t_travospublic_measure_1h":
      return {
        contextMenu: "交通量データ（最新）_１時間値_常設トラカン",
        attributeConvert: new TraVosCalcHourRealTimeTrafficConvert(
          roadType,
          regularObservationCd,
          commonName,
          timeCorrectionFlag
        ),
      };
    /** Travos_計測値５分（画像認識型） */
    case "t_travospublic_measure_5m_img":
      return {
        contextMenu: "交通量データ（最新）_５分値_CCTVトラカン",
        attributeConvert: new TraVosCCDFiveMinRealTimeTrafficConvert(
          roadType,
          regularObservationCd,
          commonName,
          timeCorrectionFlag
        ),
      };
    /** Travos_計測値１時間（画像認識型） */
    case "t_travospublic_measure_1h_img":
      return {
        contextMenu: "交通量データ（最新）_１時間値_CCTVトラカン",
        attributeConvert: new TraVosCCDHourRealTimeTrafficConvert(
          roadType,
          regularObservationCd,
          commonName,
          timeCorrectionFlag
        ),
      };
  }
};

/**
 * 選択状態のスタイルを返却
 * @param type 地物タイプ
 */
const FUNCTION_getSelcetedStyle = (type: string): Style => {
  if (type.includes("Line")) {
    return new Style({
      stroke: new Stroke({
        color: "hotpink",
        width: LINE_STRING_WIDTH,
      }),
    });
  } else {
    return new Style({
      image: new CircleStyle({
        fill: new Fill({ color: "hotpink" }),
        radius: CIRCLE_RADIUS,
      }),
    });
  }
};

/**
 * 「データ表示解除処理時」コンテキストメニュー名
 */
const CONTEXT_MENU_TEXT_HIDDEN = "最新データ交通量表示解除";

/**
 * 「データ表示処理時」コンテキストメニュー名
 */
const CONTEXT_MENU_TEXT_DISPLAY = "最新データ交通量表示";

/**
 * 右クリック対象レイヤ
 */
const RIGHT_CLICK_LAYERS = [
  "Travos_計測値５分（画像認識型）(リアルタイム)",
  "Travos_計測値１時間（画像認識型）(リアルタイム)",
  "Travos_計測値５分(リアルタイム)",
  "Travos_計測値１時間(リアルタイム)",
];

/**
 * GetMap用レイヤ物理名マッピング
 */
const GETMAP_LAYERNAME_MAP = [
  { physical: "v_travospublic_measure_5m", logical: "Travos_計測値５分" },
  { physical: "v_travospublic_measure_1h", logical: "Travos_計測値１時間" },
  { physical: "v_travospublic_define_1h", logical: "Travos_確定値１時間" },
  {
    physical: "v_travospublic_traffic_basic_sec",
    logical: "Travos_交通基本調査区間",
  },
  {
    physical: "v_travospublic_measure_5m_img",
    logical: "Travos_計測値５分（画像認識型）",
  },
  {
    physical: "v_travospublic_measure_1h_img",
    logical: "Travos_計測値１時間（画像認識型）",
  },
  {
    physical: "v_travospublic_define_1h_img",
    logical: "Travos_確定値１時間（画像認識型）",
  },
];
/**
 * TraVosレイヤ出力クラス
 */
export class TraVosLayerFactory implements ILayerFactory {
  /**
   * 表示WMSレイヤ
   */
  private _wmsLayer: ImageLayer<ImageWMSEx>;

  /**
   * 表示WMSソース
   */
  private _wmsImageSource: ImageWMSEx;

  /**
   * 表示対象TraVosレイヤ
   */
  private _visibleTraVosLayers: TraVosLayerName[] = [];

  /**
   * 再生対象選択地物
   */
  private _TraVosPlayNode: Feature;

  /**
   * 再生対象通知イベント
   */
  private _TraVosPlayEvent: ITraVosPlayEvent;

  /**
   * 地物属性の有無を取得
   */
  isFeatureInfo(): boolean {
    return true;
  }

  /**
   * 範囲地物属性の有無を取得
   */
  isRangeFeatureInfo(): boolean {
    return true;
  }

  /**
   * 右クリック処理の有無を取得
   */
  isRightClickInfo(): boolean {
    return true;
  }

  /**
   * レイヤ定義を取得
   * @param map openLayers Mapクラス
   */
  getViewLayer(_map?: Map): ImageLayer<ImageWMSEx>[] {
    if (this._wmsLayer) {
      return [this._wmsLayer];
    }
    //WMSレイヤの取得
    this._wmsImageSource = new ImageWMSEx({
      url: URL_API,
      params: {
        LAYERS: "",
        EXCEPTIONS: "application/json",
      },
      ratio: 1,
      serverType: "geoserver",
      crossOrigin: "Anonymous",
    });
    this._wmsLayer = new ImageLayer({
      source: this._wmsImageSource,
      maxZoom: LAYER_MAX_ZOOMLEVEL,
      minZoom: LAYER_MIN_ZOOMLEVEL,
    });
    return [this._wmsLayer];
  }

  /**
   * 地物情報を取得
   * @param map 地図
   * @param layerNames レイヤ名リスト
   * @param latitude クリック座標緯度
   * @param longitude クリック座標経度
   * @param x クリック位置X
   * @param y クリック位置Y
   * @param width 地図幅
   * @param height 地図高さ
   * @param bBox 地図範囲（BBOX)
   */
  async getFeatureInfo(
    map: Map,
    _latitude: number,
    _longitude: number,
    x: number,
    y: number,
    width: number,
    height: number,
    bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    if (
      map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
      map.getView().getZoom() <= LAYER_MIN_ZOOMLEVEL
    ) {
      return null;
    }
    // レイヤ論理名から、レイヤ論理名を抽出
    // 表示対象TraVosLayerNameより時間コード・道路属性を取得し、cqlパラメータクラスを作成
    const cqlParameters = this._visibleTraVosLayers.map((layerDetail) => {
      const cqlParameterString = `"道路種別" IN (${layerDetail.roadType
        .map((ele) => "'" + ele + "'")
        .join(",")}) AND "時間コード"=${layerDetail.timeCode}`;
      return new CqlParameter(
        getLayerKey(layerDetail.getLayerName()).replace("t_", "v_"),
        cqlParameterString
      );
    });
    const buffer = getLayerKey(
      this._visibleTraVosLayers[0].getLayerName()
    ).includes("traffic_basic")
      ? 5
      : 0;
    const WMSGetter = new WMSGetFeatureManager();
    const selectedFeatureGeoJSON = await WMSGetter.getInfo(
      URL_API,
      bBox.join(","),
      cqlParameters,
      x,
      y,
      height,
      width,
      buffer
    ).catch((error) => {
      throw error;
    });
    const selectedFeatures = new GeoJSON().readFeatures(selectedFeatureGeoJSON);
    if (selectedFeatures.length === 0) return null;
    const selectFeatureInfoList: SelectedFeatureInfo[] = await Promise.all(
      selectedFeatures.map(async (feature) => {
        const id = feature.getId().toString();
        const layerKey = id.split(".")[0].replace("v_", "t_");
        // 選択地物に設定するIDを定義
        const selectedId = layerKey.includes("traffic_basic")
          ? `${layerKey}-${feature.get("道路種別")}-${feature.get(
              "交通調査基本区間番号"
            )}`
          : `${layerKey}-${feature.get("道路種別")}-${feature.get(
              "常時観測点コード"
            )}`;

        const wfsCqlParameterString = layerKey.includes("traffic_basic")
          ? `"道路種別"='${feature.get(
              "道路種別"
            )}' AND "時間コード"=${feature.get(
              "時間コード"
            )} AND "交通調査基本区間番号"='${feature.get(
              "交通調査基本区間番号"
            )}'`
          : `"道路種別"='${feature.get(
              "道路種別"
            )}' AND "時間コード"=${feature.get(
              "時間コード"
            )} AND "常時観測点コード"=${feature.get("常時観測点コード")}`;

        const wfsCqlParameters = [
          new CqlParameter(layerKey, wfsCqlParameterString),
        ];

        const WFSGetter = new WFSGetFeatureManager();
        const wfsGeoJson = await WFSGetter.getInfo(
          URL_API,
          wfsCqlParameters
        ).catch((error) => {
          throw error;
        });
        const wfsFeature = new GeoJSON().readFeatures(wfsGeoJson)[0];
        if (!wfsFeature) return;
        // 表示対象レイヤがリアルタイムかどうかを判別
        const isRealTime =
          this._visibleTraVosLayers.filter((layerDetail) =>
            layerDetail.layerType.includes("リアルタイム")
          ).length > 0;
        // リアルタイム/過去判別結果に応じて属性変換クラス・attributeクラスを生成
        let attributeConvertInfo:
          | SearchDateAttributeConvertInfo
          | RealTimeAttributeConvertInfo = null;
        let attributeType: AttributeType = null;
        if (isRealTime) {
          // リアルタイム属性変換
          attributeConvertInfo = FUNCTION_getRealTimeAttributeConvert(
            layerKey,
            wfsGeoJson,
            LayerName[layerKey as keyof typeof LayerName] as TraVosLayerType,
            feature.get("道路種別").toString(),
            feature.get("常時観測点コード"),
            feature.get("通称名"),
            feature.get("収集時間フラグ（5分間／1時間）"),
            feature.get("時間コード").toString()
          );
          attributeType = AttributeType.RealTime;
        } else {
          attributeConvertInfo = FUNCTION_getSearchDateAttributeConvert(
            layerKey,
            wfsGeoJson,
            LayerName[layerKey as keyof typeof LayerName] as TraVosLayerType,
            feature.get("道路種別"),
            feature.get("常時観測点コード"),
            feature.get("通称名"),
            feature.get("収集時間フラグ（5分間／1時間）"),
            feature.get("時間コード").toString(),
            feature.get("交通調査基本区間番号")
          );
          attributeType = AttributeType.History;
        }
        // selectFeatureを生成（地物情報のみ）
        const geometry = feature.getGeometry();
        const selectFeature = new Feature({
          geometry,
        });
        selectFeature.setId(selectedId);
        selectFeature.setStyle(FUNCTION_getSelcetedStyle(geometry.getType()));
        // featureGeoJSONを生成
        const geoJSON = new GeoJSON().writeFeature(wfsFeature);
        const featureGeoJSON = new FeatureGeoJson(geoJSON, selectedId);
        // selectedFeatureInfoを生成して、リストに追加
        const selectedFeatureInfo = new SelectedFeatureInfo(
          selectFeature,
          attributeConvertInfo.contextMenu,
          featureGeoJSON,
          attributeType,
          attributeConvertInfo.attributeConvert as ISearchDateAttributeConvert,
          false,
          layerKey
        );
        return selectedFeatureInfo;
      })
    );
    return selectFeatureInfoList;
  }

  /**
   * 指定範囲の地物情報を取得
   * @param map 地図
   * @param bBox 地図範囲（BBOX)
   */
  async getRangeFeatureInfo(
    map: Map,
    bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    if (
      map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
      map.getView().getZoom() <= LAYER_MIN_ZOOMLEVEL
    ) {
      return null;
    }
    // レイヤ論理名から、レイヤ論理名を抽出
    // 表示対象TraVosLayerNameより時間コード・道路属性を取得し、cqlパラメータクラスを作成
    const cqlParameters = this._visibleTraVosLayers.map((layerDetail) => {
      const cqlParameterString = `"道路種別" IN (${layerDetail.roadType
        .map((ele) => "'" + ele + "'")
        .join(",")}) AND "時間コード"=${layerDetail.timeCode}`;
      return new CqlParameter(
        getLayerKey(layerDetail.getLayerName()),
        cqlParameterString
      );
    });
    const WFSGetter = new WFSGetFeatureManager();
    const selectedFeatureGeoJSON = await WFSGetter.getInfo(
      URL_API,
      cqlParameters,
      bBox
    ).catch((error) => {
      throw error;
    });
    const selectedFeatures = new GeoJSON().readFeatures(selectedFeatureGeoJSON);
    if (selectedFeatures.length === 0) return null;
    const selectFeatureInfoList: SelectedFeatureInfo[] = selectedFeatures.map(
      (feature) => {
        const id = feature.getId().toString();
        const layerKey = id.split(".")[0].replace("v_", "t_");
        // 表示対象レイヤがリアルタイムかどうかを判別
        const isRealTime =
          this._visibleTraVosLayers.filter((layerDetail) =>
            layerDetail.layerType.includes("リアルタイム")
          ).length > 0;
        // リアルタイム/過去判別結果に応じて属性変換クラス・attributeクラスを生成
        let attributeConvertInfo:
          | SearchDateAttributeConvertInfo
          | RealTimeAttributeConvertInfo = null;
        let attributeType: AttributeType = null;

        const selecetedGeoJSON = new GeoJSON().writeFeature(feature);
        if (isRealTime) {
          // リアルタイム属性変換(本番用)
          attributeConvertInfo = FUNCTION_getRealTimeAttributeConvert(
            layerKey,
            selecetedGeoJSON,
            LayerName[layerKey as keyof typeof LayerName] as TraVosLayerType,
            feature.get("道路種別").toString(),
            feature.get("常時観測点コード"),
            feature.get("通称名"),
            feature.get("収集時間フラグ（5分間／1時間）"),
            feature.get("時間コード").toString()
          );
          attributeType = AttributeType.RealTime;
        } else {
          attributeConvertInfo = FUNCTION_getSearchDateAttributeConvert(
            layerKey,
            selecetedGeoJSON,
            LayerName[layerKey as keyof typeof LayerName] as TraVosLayerType,
            feature.get("道路種別"),
            feature.get("常時観測点コード"),
            feature.get("通称名"),
            feature.get("収集時間フラグ（5分間／1時間）"),
            feature.get("時間コード").toString(),
            feature.get("交通調査基本区間番号")
          );

          attributeType = AttributeType.History;
        }
        // selectFeatureを生成（地物情報のみ）
        const geometry = feature.getGeometry();

        const selectFeature = new Feature({
          geometry,
        });

        const geoJSON = new GeoJSON().writeFeature(feature);
        const featureGeoJSON = new FeatureGeoJson(geoJSON, id);
        // selectedFeatureInfoを生成して、リストに追加
        const selectedFeatureInfo = new SelectedFeatureInfo(
          selectFeature,
          attributeConvertInfo.contextMenu,
          featureGeoJSON,
          attributeType,
          attributeConvertInfo.attributeConvert as ISearchDateAttributeConvert,
          false
        );
        return selectedFeatureInfo;
      }
    );
    return selectFeatureInfoList;
  }

  /**
   * 地物属性の有無を取得
   */
  async getRightClickInfo(
    _map: Map,
    _latitude: number,
    _longitude: number,
    x: number,
    y: number,
    width: number,
    height: number,
    bBox: number[]
  ): Promise<MapContextMenuItem[]> {
    if (
      _map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
      _map.getView().getZoom() < LAYER_MIN_ZOOMLEVEL
    ) {
      return null;
    }
    // 表示レイヤが対象レイヤかどうかを確認
    const filteredLayerNames: TraVosLayerName[] = [];
    this._visibleTraVosLayers.forEach((layerName) => {
      if (RIGHT_CLICK_LAYERS.includes(layerName.layerType)) {
        filteredLayerNames.push(layerName);
      }
    });
    if (filteredLayerNames.length === 0) return null;
    // 表示対象TraVosLayerNameより時間コード・道路属性を取得し、cqlパラメータクラスを作成
    const cqlParameters = this._visibleTraVosLayers.map((layerDetail) => {
      const cqlParameterString = `"道路種別" IN (${layerDetail.roadType
        .map((ele) => "'" + ele + "'")
        .join(",")}) AND "時間コード"= ${layerDetail.timeCode}`;
      return new CqlParameter(
        getLayerKey(layerDetail.getLayerName()),
        cqlParameterString
      );
    });
    const buffer = getLayerKey(
      this._visibleTraVosLayers[0].getLayerName()
    ).includes("traffic_basic")
      ? 5
      : 0;
    const WMSGetter = new WMSGetFeatureManager();
    const geoJSON = await WMSGetter.getInfo(
      URL_API,
      bBox.join(","),
      cqlParameters,
      x,
      y,
      height,
      width,
      buffer
    ).catch((error) => {
      throw error;
    });

    // 取得した地物IDが再生対象選択の地物IDと一致するか
    const features = new GeoJSON().readFeatures(geoJSON);
    const mapContextMenuItems: MapContextMenuItem[] = await Promise.all(
      features.map(async (feature) => {
        // 地物が取得できなかった場合、null返却
        if (!feature) return null;
        const id = feature.getId().toString();
        const layerKey = feature
          .getId()
          .toString()
          .split(".")[0]
          .replace("v_", "t_");
        const selectedId =
          layerKey === "t_travospublic_traffic_basic_sec"
            ? `${layerKey}-${feature.get("道路種別")}-${feature.get(
                "交通調査基本区間番号"
              )}`
            : `${layerKey}-${feature.get("道路種別")}-${feature.get(
                "常時観測点コード"
              )}`;
        const wfsCqlParameterString = layerKey.includes("traffic_basic")
          ? `"道路種別"='${feature.get(
              "道路種別"
            )}' AND "時間コード"=${feature.get(
              "時間コード"
            )} AND "交通調査基本区間番号"='${feature.get(
              "交通調査基本区間番号"
            )}'`
          : `"道路種別"='${feature.get(
              "道路種別"
            )}' AND "時間コード"=${feature.get(
              "時間コード"
            )} AND "常時観測点コード"=${feature.get("常時観測点コード")}`;

        const wfsCqlParameters = [
          new CqlParameter(layerKey, wfsCqlParameterString),
        ];

        const WFSGetter = new WFSGetFeatureManager();
        const wfsGeoJson = await WFSGetter.getInfo(
          URL_API,
          wfsCqlParameters
        ).catch((error) => {
          throw error;
        });
        const wfsFeature = new GeoJSON()
          .readFeatures(wfsGeoJson)
          .find((feature) => feature.getId() === id);
        if (!wfsFeature) return;
        wfsFeature.setId(selectedId);
        if (
          this._TraVosPlayNode &&
          selectedId === this.getTraVosPlayNodeFid()
        ) {
          // 一致する場合：「データ表示解除処理」
          return new MapContextMenuItem(
            CONTEXT_MENU_TEXT_HIDDEN,
            this._TraVosPlayEvent.onTraVosNodeUnSelected,
            true,
            null,
            this.getTraVosPlayNodeFid()
          );
        } else {
          // 一致していない場合：「データ表示処理」
          wfsFeature.setStyle(
            FUNCTION_getSelcetedStyle(wfsFeature.getGeometry().getType())
          );
          // 前回選択地物があるか
          if (this._TraVosPlayNode) {
            // ある場合
            return new MapContextMenuItem(
              CONTEXT_MENU_TEXT_DISPLAY,
              () => {
                this._TraVosPlayNode = wfsFeature;
                this._TraVosPlayEvent.onTraVosNodeSelected(
                  String(selectedId),
                  FUNCTION_getRealTimeTrafficConvert(
                    layerKey,
                    feature.get("道路種別"),
                    feature.get("常時観測点コード"),
                    feature.get("通称名"),
                    feature.get("収集時間フラグ（5分間／1時間）")
                  ).attributeConvert
                )();
              },
              false,
              wfsFeature,
              this.getTraVosPlayNodeFid()
            );
          } else {
            // ない場合
            return new MapContextMenuItem(
              CONTEXT_MENU_TEXT_DISPLAY,
              () => {
                this._TraVosPlayNode = wfsFeature;
                this._TraVosPlayEvent.onTraVosNodeSelected(
                  String(selectedId),
                  FUNCTION_getRealTimeTrafficConvert(
                    layerKey,
                    feature.get("道路種別"),
                    feature.get("常時観測点コード"),
                    feature.get("通称名"),
                    feature.get("収集時間フラグ（5分間／1時間）")
                  ).attributeConvert
                )();
              },
              false,
              wfsFeature
            );
          }
        }
      }, this)
    );
    return mapContextMenuItems;
  }

  /**
   * レイヤの表示状態を設定
   * @param isVisible 表示有無
   * @param layerNameList 対象TraVosLayerリスト
   */
  setVisible(isVisible: boolean, layerNameList: TraVosLayerName[]) {
    this._visibleTraVosLayers = layerNameList;
    const layerKeys: string[] = [];
    const cqlParameters: string[] = [];
    if (this._visibleTraVosLayers.length !== 0) {
      this._visibleTraVosLayers.forEach((layerDetail) => {
        if (layerDetail.roadType == null) {
          return;
        }
        const cqlParameter = `"道路種別" IN (${layerDetail.roadType
          .map((ele) => "'" + ele + "'")
          .join(",")}) AND "時間コード"= ${layerDetail.timeCode}`;
        const layerKey = GETMAP_LAYERNAME_MAP.find(
          (obj) => obj.logical === layerDetail.getLayerName()
        ).physical;
        layerKeys.push(layerKey);
        cqlParameters.push(cqlParameter);
      });
    }
    if (cqlParameters.length == 0) {
      this._wmsLayer.setVisible(false);
    } else {
      this._wmsImageSource.updateParams({
        LAYERS: layerKeys.join(","),
        EXCEPTIONS: "application/json",
        CQL_FILTER: cqlParameters.join(";"),
      });
      this._wmsLayer.setVisible(isVisible);
    }
  }

  /**
   * 再生対象選択イベントを設定
   * @param traVosPlayEvent 地点選択コールバック関数
   */
  setTraVosPlayEvent(traVosPlayEvent: ITraVosPlayEvent): void {
    this._TraVosPlayEvent = traVosPlayEvent;
  }

  /**
   * 再生対象選択の地物IDを取得
   * @param traVosPlayEvent 地点選択コールバック関数
   */
  getTraVosPlayNodeFid(): string {
    return this._TraVosPlayNode.getId().toString();
  }

  /**
   * 表示中のレイヤ数を取得
   */
  getVisibleLayerCount() {
    return this._wmsLayer?.getVisible() ? this._visibleTraVosLayers.length : 0;
  }

  /**
   * 表示レイヤが存在するか判定
   */
  isVisibleLayer(_map: Map) {
    return LayerUtil.isVisibleLayer(_map, this._wmsLayer);
  }

  /**
   * 再生対象選択を設定
   */
  setTraVosPlayNode(feature: Feature): void {
    this._TraVosPlayNode = feature;
  }

  /**
   * 再生対象選択を解除
   */
  clearTraVosPlayNode(): void {
    this._TraVosPlayNode = null;
  }

  /**
   * リアルタイム属性からリアルタイム交通量を取得
   */
  getRealtimeTrafficConvertBySearchDateAttribute(
    searchDateAttributeConvert: ISearchDateAttributeConvert
  ): IRealTimeTrafficConvert {
    const feature = new GeoJSON().readFeatures(
      searchDateAttributeConvert.getGeoJSON()
    )[0];
    const convertInfo = FUNCTION_getRealTimeTrafficConvert(
      feature?.getId().toString().split(".")[0],
      feature?.getProperties().道路種別,
      feature?.getProperties().常時観測点コード,
      feature?.getProperties().通称名,
      feature?.getProperties()["収集時間フラグ（5分間／1時間）"]
    );
    return convertInfo.attributeConvert;
  }
}
